import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, MessageCircle } from 'lucide-react';
import { confirmCardPayment } from '../lib/booking';
import { forgetCardCheckout } from '../lib/cardCheckout';
import {
  trackCardCheckoutReturn,
  trackCardPaymentFailed,
  trackWhatsAppClick,
} from '../lib/analytics';
import { whatsappLink } from '../data/site';

/**
 * Viva's failure redirect. Nothing was charged, but the held slot is still
 * booked out on LinkTwist until we release it — so this pings the callable,
 * which cancels the hold once Viva confirms the payment didn't go through.
 */
export function BookingFailedPage() {
  const [params] = useSearchParams();
  const orderCode = params.get('s');
  const transactionId = params.get('t');

  useEffect(() => {
    trackCardCheckoutReturn('failed', orderCode ?? '');
    trackCardPaymentFailed(
      orderCode,
      'Returned to the failure URL from Smart Checkout',
    );
    forgetCardCheckout();
    if (!orderCode) return;
    void confirmCardPayment({
      orderCode,
      ...(transactionId ? { transactionId } : {}),
    }).catch(() => {
      // Best effort — the hold also expires on its own, and the webhook
      // releases it when Viva reports the failed transaction.
    });
  }, [orderCode, transactionId]);

  return (
    <main className="mx-auto flex min-h-[70vh] w-full max-w-xl flex-col justify-center px-6 py-20 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-crimson/10">
        <AlertCircle className="h-7 w-7 text-crimson" />
      </div>
      <h1 className="mt-5 font-display text-3xl font-medium text-charcoal">
        Payment not completed
      </h1>
      <p className="mx-auto mt-3 max-w-md font-sans text-sm font-light leading-relaxed text-stone">
        Your card was not charged and nothing has been reserved. You can start
        again, or message us and we&rsquo;ll take care of the booking with you.
      </p>
      <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <Link to="/experiences" className="btn btn-primary">
          Try again
        </Link>
        <a
          href={whatsappLink(
            "Hi Flying Muse! My card payment didn't go through — could you help me book?",
          )}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => trackWhatsAppClick('payment_failed')}
          className="inline-flex items-center gap-2 font-sans text-sm font-medium text-charcoal/70 transition-colors hover:text-crimson"
        >
          <MessageCircle className="h-4 w-4" />
          Message us on WhatsApp
        </a>
      </div>
    </main>
  );
}
